import { useEffect, type ReactElement } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTopHeader } from 'app/hooks';
import { useWebBuilderAccess } from '../../hooks/useWebBuilderAccess';
import usePlusWebsites from '../../hooks/usePlusWebsites';
import { useOnboarding } from './store/onboarding-store';
import { ONBOARDING_PATHS, ONBOARDING_SEGMENTS, type OnboardingSegment } from './constants/paths';

function getFirstIncompleteStep(state: {
  primaryColor: string;
  logoUrl: string;
  landingType: 'in-app' | 'external';
  externalHomepageUrl: string;
}): OnboardingSegment | null {
  if (!state.primaryColor) return ONBOARDING_SEGMENTS.colour;
  if (!state.logoUrl) return ONBOARDING_SEGMENTS.logo;
  if (state.landingType === 'external' && !state.externalHomepageUrl.trim()) {
    return ONBOARDING_SEGMENTS.homepage;
  }
  return null;
}

const STEP_ORDER: OnboardingSegment[] = [
  ONBOARDING_SEGMENTS.colour,
  ONBOARDING_SEGMENTS.logo,
  ONBOARDING_SEGMENTS.preview,
  ONBOARDING_SEGMENTS.homepage,
  ONBOARDING_SEGMENTS.domain,
];

function getStepRedirect(
  segment: string,
  state: {
    primaryColor: string;
    logoUrl: string;
    landingType: 'in-app' | 'external';
    externalHomepageUrl: string;
  }
): string | null {
  const currentIndex = STEP_ORDER.indexOf(segment as OnboardingSegment);
  if (currentIndex === -1) return null;

  const incomplete = getFirstIncompleteStep(state);
  if (!incomplete || incomplete === segment) return null;

  const incompleteIndex = STEP_ORDER.indexOf(incomplete);
  if (currentIndex <= incompleteIndex) return null;

  return ONBOARDING_PATHS[incomplete];
}

// Pages only shown during upsell (no builder access)
const UPSELL_ONLY_SEGMENTS = new Set<string>([
  '', // index (sales page)
  ONBOARDING_SEGMENTS.steps,
]);

const OnboardingOutlet = (): ReactElement => {
  useTopHeader('Website builder');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlOrgId = searchParams.get('organisationId') ?? '';
  const {
    organisationId,
    primaryColor,
    logoUrl,
    landingType,
    externalHomepageUrl,
    websiteCreated,
    isUpsellJourney,
    setOrganisationId,
    setIsUpsellJourney,
    reset,
  } = useOnboarding();
  const {
    hasWebBuilderAccess,
    isHidden: builderHidden,
    isLoading: accessLoading,
    isError: accessError,
  } = useWebBuilderAccess();
  const { plusWebsites, isLoading: websitesLoading } = usePlusWebsites();

  useEffect(() => {
    if (!urlOrgId) return;
    if (organisationId && organisationId !== urlOrgId) {
      reset();
      // Navigate to base and let the routing guard redirect once the new org's
      // access state has loaded — hasWebBuilderAccess is stale at this point.
      navigate(
        { pathname: ONBOARDING_PATHS.base, search: searchParams.toString() },
        { replace: true }
      );
    }
    setOrganisationId(urlOrgId);
  }, [urlOrgId, organisationId, setOrganisationId, reset, navigate, searchParams]);

  // Capture the journey type once when access state is known — persists in the store
  // so it survives access flipping mid-flow (e.g. after upgrade).
  // On error, default to upsell so the routing guard can still act.
  useEffect(() => {
    if (accessLoading || isUpsellJourney !== null) return;
    if (accessError) {
      setIsUpsellJourney(true);
      return;
    }
    setIsUpsellJourney(!hasWebBuilderAccess);
  }, [accessLoading, accessError, isUpsellJourney, hasWebBuilderAccess, setIsUpsellJourney]);

  const segment = location.pathname.replace(`${ONBOARDING_PATHS.base}/`, '').split('/')[0];
  const isAtBase = location.pathname.replace(/\/$/, '') === ONBOARDING_PATHS.base;
  const currentSegment = isAtBase ? '' : segment;
  const isLoading = accessLoading || websitesLoading;
  const onboardingDone = plusWebsites.length > 0;

  // A finished journey stays in the store so its success page keeps its authorisation (guard 3
  // below). The moment the user is anywhere else in onboarding that journey is history, so its
  // answers are cleared and the guards below see a fresh start.
  useEffect(() => {
    if (websiteCreated && currentSegment !== ONBOARDING_SEGMENTS.success) {
      reset();
    }
  }, [websiteCreated, currentSegment, reset]);

  // Single consolidated routing guard — priority order prevents competing navigations.
  // Wait for the journey type to be resolved before acting — after an org switch the
  // store resets isUpsellJourney to null and the access queries refetch, so acting
  // before that settles would use stale access data from the previous org.
  useEffect(() => {
    if (isLoading || isUpsellJourney === null || builderHidden) return;

    // 1. Org already has a website + access → go to builder
    //    A journey that just finished holds this back until its success page is left behind, so
    //    the user reads it and leaves through its own button rather than being redirected away.
    if (onboardingDone && hasWebBuilderAccess && !websiteCreated) {
      navigate({ pathname: '/plus-websites', search: searchParams.toString() }, { replace: true });
      return;
    }

    // 2. Users with access skip upsell pages (sales, steps overview)
    if (hasWebBuilderAccess && UPSELL_ONLY_SEGMENTS.has(currentSegment)) {
      navigate(
        { pathname: ONBOARDING_PATHS.colour, search: searchParams.toString() },
        { replace: true }
      );
      return;
    }

    // 3. Success page requires the creation signal — direct links are redirected
    if (currentSegment === ONBOARDING_SEGMENTS.success) {
      if (!websiteCreated) {
        const startPath = hasWebBuilderAccess ? ONBOARDING_PATHS.colour : ONBOARDING_PATHS.base;
        navigate({ pathname: startPath, search: searchParams.toString() }, { replace: true });
      }
      return;
    }

    // 4. Step order — can't skip ahead past incomplete steps
    const stepRedirect = getStepRedirect(currentSegment, {
      primaryColor,
      logoUrl,
      landingType,
      externalHomepageUrl,
    });
    if (stepRedirect) {
      navigate({ pathname: stepRedirect, search: searchParams.toString() }, { replace: true });
    }
  }, [
    isLoading,
    isUpsellJourney,
    builderHidden,
    onboardingDone,
    hasWebBuilderAccess,
    websiteCreated,
    currentSegment,
    primaryColor,
    logoUrl,
    landingType,
    externalHomepageUrl,
    plusWebsites.length,
    navigate,
    searchParams,
  ]);

  if (!urlOrgId) return <Navigate to="/plus-websites" replace />;
  // Switched off for this organisation: no sales page or free preview to offer it.
  if (!accessLoading && builderHidden) return <Navigate to="/" replace />;

  // No SnackbarProvider here — see PlusWebsitesLayout: a nested provider hijacks the global
  // enqueueSnackbar queryClient toasts through and never gives it back.
  return <Outlet />;
};

export default OnboardingOutlet;
